import { GridColDef, GridSortCellParams } from "@mui/x-data-grid-pro"
import {
  ScoreRecord,
  StudentAcademicStatusDTO,
} from "../../studentAcademicStatus.dto"
import { Box, Tooltip, Typography } from "@mui/material"
import clsx from "clsx"
import { scoreRecordSorter } from "./column.sort.comparators"
import { ColumnKeys } from "./column.keys"
import { formatScoreAge, isScoreStale } from "../../scoreRecency"

// Cell content is `score · age` (e.g. "2 · 3w"). No trend arrow ("was N"):
// the DTO carries only the last ScoreRecord per metric, so a previous score
// does not exist on the frontend yet.
function renderScoreCell(record: ScoreRecord | null) {
  if (!record) return null
  const stale = isScoreStale(record.submittedAt)
  const age = formatScoreAge(record.submittedAt)
  const timeString = new Date(record.submittedAt).toLocaleString()
  return (
    <Tooltip placement="right-end" title={`Submitted at ` + timeString}>
      <Box
        display="flex"
        sx={{
          height: "100%",
          px: "10px",
          justifyContent: "left",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: stale ? "text.disabled" : undefined }}
        >
          {age ? `${record.score} · ${age}` : record.score}
        </Typography>
      </Box>
    </Tooltip>
  )
}

// The comparator needs the full ScoreRecord (staleness lives on submittedAt),
// while the cell value stays the bare score for filtering and export.
function rowFromSortParams(
  params: GridSortCellParams,
): StudentAcademicStatusDTO | null {
  return params.api.getRow(params.id)
}

export const CSAT_COLUMNS: GridColDef<StudentAcademicStatusDTO>[] = [
  {
    field: ColumnKeys.LAST_CSAT_SCORE,
    // Short header on purpose: "Last CSAT score" truncated to "Last CSAT
    // Sc…" at the width the viewport can afford (Mayer read "aps score" off
    // the NPS one) — the description carries the long name into the header
    // tooltip instead.
    headerName: "CSAT",
    description: "Last CSAT score, shown as score · age",
    type: "number",
    width: 100,
    valueGetter: (value, row) => row.status.satisfaction.csat?.score,
    getSortComparator(sortDirection) {
      return (v1, v2, cellParams1, cellParams2) =>
        scoreRecordSorter(
          rowFromSortParams(cellParams1)?.status.satisfaction.csat,
          rowFromSortParams(cellParams2)?.status.satisfaction.csat,
          sortDirection,
        )
    },
    cellClassName: (params) => {
      const record = params.row.status.satisfaction.csat
      const stale = record ? isScoreStale(record.submittedAt) : false
      return clsx("cell", {
        "is-flagged": !stale && params.value < 4,
        "is-stale": stale,
      })
    },
    renderCell: (params) =>
      renderScoreCell(params.row.status.satisfaction.csat),
  },
  {
    field: ColumnKeys.LAST_NPS_SCORE,
    headerName: "NPS",
    description: "Last NPS score, shown as score · age",
    width: 100,
    type: "number",
    valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
      return studentRow.status.satisfaction.nps?.score
    },
    getSortComparator(sortDirection) {
      return (v1, v2, cellParams1, cellParams2) =>
        scoreRecordSorter(
          rowFromSortParams(cellParams1)?.status.satisfaction.nps,
          rowFromSortParams(cellParams2)?.status.satisfaction.nps,
          sortDirection,
        )
    },
    cellClassName: (params) => {
      const record = params.row.status.satisfaction.nps
      const stale = record ? isScoreStale(record.submittedAt) : false
      return clsx("cell", {
        "is-flagged": !stale && params.value < 8,
        "is-stale": stale,
      })
    },
    renderCell: (params) => renderScoreCell(params.row.status.satisfaction.nps),
  },
]
